import { useEffect } from 'react';
import './Translate.css';

const SCRIPT_ID = 'google-translate-script';
const ELEMENT_ID = 'google_translate_element';
const CALLBACK_NAME = 'googleTranslateElementInit';
const SOURCE_LANGUAGE = 'en';

let initializationPromise = null;
let pendingLanguage = null;
let selectListenerTarget = null;
let selectWaitInterval = null;
let programmaticChange = false;

const normalizeLanguage = (language) => {
    if (
        typeof language !== 'string' ||
        !language.trim()
    ) {
        return SOURCE_LANGUAGE;
    }

    return language.trim().toLowerCase();
};

export const setCookie = (
    cookieName,
    cookieValue,
    expirationDays
) => {
    const expirationDate = new Date();

    expirationDate.setTime(
        expirationDate.getTime() +
        expirationDays * 24 * 60 * 60 * 1000
    );

    document.cookie = [
        `${cookieName}=${cookieValue}`,
        `expires=${expirationDate.toUTCString()}`,
        'path=/',
        'SameSite=Lax',
    ].join(';');
};

const persistLanguage = (language) => {
    localStorage.setItem(
        'selectedLanguage',
        language
    );

    setCookie(
        'googtrans',
        `/${SOURCE_LANGUAGE}/${language}`,
        7
    );

    window.dispatchEvent(
        new CustomEvent(
            'leedana:language-change',
            {
                detail: {
                    language,
                },
            }
        )
    );
};

const ensureTranslateElement = () => {
    let element =
        document.getElementById(ELEMENT_ID);

    if (element) {
        return element;
    }

    element = document.createElement('div');
    element.id = ELEMENT_ID;
    element.className = 'custom-translate';
    element.setAttribute('aria-hidden', 'true');

    // Keep Google's required element available without
    // displaying its built-in selector.
    Object.assign(element.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
    });

    document.body.appendChild(element);

    return element;
};

const getTranslateSelect = () =>
    document.querySelector(
        `#${ELEMENT_ID} .goog-te-combo`
    ) ||
    document.querySelector('.goog-te-combo');

const handleWidgetLanguageChange = (event) => {
    const language = normalizeLanguage(
        event.target.value
    );

    pendingLanguage = null;

    if (!programmaticChange) {
        persistLanguage(language);
    }
};

const attachSelectListener = (select) => {
    if (selectListenerTarget === select) {
        return;
    }

    if (selectListenerTarget) {
        selectListenerTarget.removeEventListener(
            'change',
            handleWidgetLanguageChange
        );
    }

    select.addEventListener(
        'change',
        handleWidgetLanguageChange
    );

    selectListenerTarget = select;
};

const applyLanguageToWidget = (language) => {
    const select = getTranslateSelect();

    if (
        !select ||
        !select.options ||
        select.options.length === 0
    ) {
        return false;
    }

    attachSelectListener(select);

    const optionExists = Array.from(
        select.options
    ).some(
        (option) => option.value === language
    );

    if (!optionExists) {
        return false;
    }

    if (select.value !== language) {
        programmaticChange = true;

        try {
            select.value = language;

            select.dispatchEvent(
                new Event('change', {
                    bubbles: true,
                })
            );
        } finally {
            programmaticChange = false;
        }
    }

    pendingLanguage = null;

    return true;
};

export const changeGoogleTranslateLanguage = (
    language
) => {
    const normalizedLanguage =
        normalizeLanguage(language);

    pendingLanguage = normalizedLanguage;

    persistLanguage(normalizedLanguage);

    return applyLanguageToWidget(
        normalizedLanguage
    );
};

const waitForTranslateSelect = () => {
    if (selectWaitInterval) {
        window.clearInterval(
            selectWaitInterval
        );
    }

    let attempts = 0;
    const maximumAttempts = 100;

    const checkForSelect = () => {
        attempts += 1;

        const select = getTranslateSelect();

        if (
            select &&
            select.options &&
            select.options.length > 0
        ) {
            attachSelectListener(select);

            const language =
                pendingLanguage ||
                localStorage.getItem(
                    'selectedLanguage'
                ) ||
                SOURCE_LANGUAGE;

            applyLanguageToWidget(
                normalizeLanguage(language)
            );

            window.clearInterval(
                selectWaitInterval
            );

            selectWaitInterval = null;

            return;
        }

        if (attempts >= maximumAttempts) {
            window.clearInterval(
                selectWaitInterval
            );

            selectWaitInterval = null;

            console.warn(
                'Google Translate selector did not become ready.'
            );
        }
    };

    checkForSelect();

    if (!getTranslateSelect()) {
        selectWaitInterval =
            window.setInterval(
                checkForSelect,
                100
            );
    }
};

const loadGoogleTranslateScript = () => {
    const isGoogleTranslateReady = () =>
        Boolean(
            window.google?.translate
                ?.TranslateElement
        );

    if (isGoogleTranslateReady()) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        let settled = false;
        let pollId = null;
        let timeoutId = null;
        let script =
            document.getElementById(
                SCRIPT_ID
            );

        const cleanup = () => {
            if (pollId) {
                window.clearInterval(
                    pollId
                );
            }

            if (timeoutId) {
                window.clearTimeout(
                    timeoutId
                );
            }

            if (script) {
                script.removeEventListener(
                    'load',
                    handleLoad
                );

                script.removeEventListener(
                    'error',
                    handleError
                );
            }
        };

        const finish = () => {
            if (settled) {
                return true;
            }

            if (!isGoogleTranslateReady()) {
                return false;
            }

            settled = true;
            cleanup();
            resolve();

            return true;
        };

        const fail = (message) => {
            if (settled) {
                return;
            }

            // Check one final time before
            // treating the initialization as failed.
            if (finish()) {
                return;
            }

            settled = true;
            cleanup();

            reject(
                new Error(message)
            );
        };

        function handleLoad() {
            // The script can finish downloading
            // before Google's global object is ready.
            // Polling below will finish initialization.
            finish();
        }

        function handleError() {
            fail(
                'Google Translate script failed to load.'
            );
        }

        window[CALLBACK_NAME] = () => {
            finish();
        };

        // Do not depend only on Google's callback.
        // Check until TranslateElement is actually ready.
        pollId = window.setInterval(
            () => {
                finish();
            },
            100
        );

        timeoutId = window.setTimeout(
            () => {
                fail(
                    'Google Translate did not become ready within 20 seconds.'
                );
            },
            20000
        );

        if (!script) {
            script =
                document.createElement(
                    'script'
                );

            script.id = SCRIPT_ID;
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;

            script.src =
                `https://translate.google.com/translate_a/element.js?cb=${CALLBACK_NAME}`;

            script.addEventListener(
                'load',
                handleLoad
            );

            script.addEventListener(
                'error',
                handleError
            );

            document.body.appendChild(
                script
            );
        } else {
            script.addEventListener(
                'load',
                handleLoad
            );

            script.addEventListener(
                'error',
                handleError
            );

            // It may already have loaded before
            // these listeners were attached.
            finish();
        }
    });
};

const initializeGoogleTranslate = async () => {
    const translateElement =
        ensureTranslateElement();

    await loadGoogleTranslateScript();

    if (
        !translateElement.dataset
            .leedanaInitialized
    ) {
        new window.google.translate.TranslateElement(
            {
                pageLanguage: SOURCE_LANGUAGE,
                autoDisplay: false,
            },
            ELEMENT_ID
        );

        translateElement.dataset
            .leedanaInitialized = 'true';
    }

    waitForTranslateSelect();
};

const ensureGoogleTranslateInitialized = () => {
    if (!initializationPromise) {
        initializationPromise =
            initializeGoogleTranslate().catch(
                (error) => {
                    initializationPromise =
                        null;

                    throw error;
                }
            );
    }

    return initializationPromise;
};

const GoogleTranslate = ({
    selectedLanguage,
}) => {
    useEffect(() => {
        let active = true;

        ensureGoogleTranslateInitialized().catch(
            (error) => {
                if (active) {
                    console.error(
                        'Google Translate initialization failed:',
                        error
                    );
                }
            }
        );

        return () => {
            active = false;

            // Do not remove Google's script or container.
            // They are shared by the entire application.
        };
    }, []);

    useEffect(() => {
        if (selectedLanguage) {
            changeGoogleTranslateLanguage(
                selectedLanguage
            );
        }
    }, [selectedLanguage]);

    return null;
};

export default GoogleTranslate;